import React, { useCallback, useState } from 'react';

import { link, routes } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type {
  DocumentStatusFilterType,
  RepositoryDocumentsView,
  RepositoryView,
} from 'src/components';
import {
  ContentWrapper,
  DocumentStatusFilter,
  RepositoryAllDocuments,
  RepositoryTrash,
} from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  allDocuments: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0, // Critical for DataGrid v7 scrolling
  },
  page: {
    display: 'flex',
    height: '100%',
    position: 'relative',
    width: '100%',
  },
}));

const { toRelativePath: toRelativeRepositoryPath } = createRelativeRouter(routes.RepositoryIndex, {
  nestedRoutes: true,
});

export function RepositoryPage(): JSX.Element {
  const { classes } = useStyles();
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Repository</title>
      </Helmet>
      <div className={classes.page}>
        {/*
          The whole location, not just `pathname`: `<Routes location>` replaces the location
          context for every descendant, and the Documents grid reads the tab's one-shot
          `?filter=` deep link from `useLocation().search`.
        */}
        <Routes location={location}>
          <Route
            element={<Repository />}
            path={toRelativeRepositoryPath(routes.RepositoryDocuments)}
          />
          <Route element={<Navigate replace to={link.RepositoryDocuments()} />} path="/" />
        </Routes>
      </div>
    </>
  );
}

function Repository(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatusFilterType>(
    DocumentStatusFilter.All
  );
  const [documentsView, setDocumentsView] = useState<RepositoryDocumentsView>('recently-created');
  const [isTrashView, setIsTrashView] = useState(false);
  const [restoredDocumentsVersion, setRestoredDocumentsVersion] = useState(0);

  const handleRepositoryViewChange = useCallback((view: RepositoryView) => {
    setIsTrashView(view === 'trash');
    if (view !== 'trash') setDocumentsView(view);
  }, []);
  const handleDocumentsRestored = useCallback(
    () => setRestoredDocumentsVersion(version => version + 1),
    []
  );

  if (!currentUser?.myRepositoryId) return <ContentWrapper />;

  return (
    <ContentWrapper>
      {/*
        Hidden rather than unmounted while Trash is open, so the documents grid
        keeps its grouping, expanded rows and scroll position.
      */}
      <React.Activity mode={isTrashView ? 'hidden' : 'visible'}>
        <div className={classes.allDocuments}>
          <RepositoryAllDocuments
            activeRepositoryView={documentsView}
            documentStatusFilter={documentStatusFilter}
            repositoryId={currentUser.myRepositoryId}
            restoredDocumentsVersion={restoredDocumentsVersion}
            setDocumentStatusFilter={setDocumentStatusFilter}
            onRepositoryViewChange={handleRepositoryViewChange}
          />
        </div>
      </React.Activity>
      {isTrashView && (
        <div className={classes.allDocuments}>
          <RepositoryTrash
            repositoryId={currentUser.myRepositoryId}
            onDocumentsRestored={handleDocumentsRestored}
            onRepositoryViewChange={handleRepositoryViewChange}
          />
        </div>
      )}
    </ContentWrapper>
  );
}
